import { FormikSubmit } from "@/components/Button";
import { useCaptcha } from "@/components/CaptchaComponent";
import ErrorField from "@/components/ErrorField";
import { FieldInput } from "@/components/Input";
import InputCaptcha from "@/components/InputCaptcha";
import ModalComponent, { ModalHeader } from "@/components/Modal";
import { handleApiErr, useAPI } from "@/lib/api";
import { micrositePrefix } from "@/vars/assetUrl";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export const useProtected = ({
  micrositeId,
  componentId,
  revealedInfo = false,
  message = "",
  isBuilder,
  onSuccess,
}) => {
  const popupRef = useRef<Window | null>(null);
  const timeoutRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);

  const onReceive = (e) => {
    const _data = e.data;
    if (_data.from === "microsite_protected" && _data.action === "READY") {
      popupRef.current?.postMessage(
        {
          from: "microsite_protected",
          action: "SYNC",
          data: { micrositeId, componentId, message, revealedInfo },
        },
        "*"
      );
    }

    if (_data.from === "microsite_protected" && _data.action === "SYNC") {
      if (
        _data.data.componentId === componentId &&
        revealedInfo === _data.data.revealedInfo
      ) {
        onSuccess(_data.data);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", onReceive);
    return () => {
      window.removeEventListener("message", onReceive);
    };
  }, [revealedInfo]);

  useEffect(() => {
    timeoutRef.current = setInterval(() => {
      if (popupRef?.current?.closed) {
        clearInterval(timeoutRef.current);
        setIsOpen(false);
      }
    }, 500);

    return () => {
      clearInterval(timeoutRef.current);
    };
  }, [isOpen]);

  return {
    onRequest: () => {
      if (isOpen) return;
      const winHeight = 400;
      const winWidth = 450;
      const winTop = screen.height / 2 - winHeight / 2;
      const winLeft = screen.width / 2 - winWidth / 2;
      popupRef.current = window.open(
        isBuilder
          ? "/preview/protected"
          : `${micrositePrefix}@preview/protected`,
        "cv-container",
        "top=" +
          winTop +
          ",left=" +
          winLeft +
          ",toolbar=0,status=0,width=" +
          winWidth +
          ",height=" +
          winHeight
      );

      setIsOpen(true);
    },
  };
};

const ProtectedModal = ({
  modalProp,
  micrositeId,
  componentId,
  message,
  onSuccess,
}) => {
  const api = useAPI();
  const captchaProp = useCaptcha();

  const onSubmit = async (values, { setErrors, setFieldValue }) => {
    if (values.captcha === "") {
      return;
    }
    try {
      const data = await api.post(`/microsite/protected`, {
        microsite_id: micrositeId,
        component_id: componentId,
        password: values.password,
        captcha: values.captcha,
      });
      toast.success("Password is correct!");
      modalProp.close();
      onSuccess(data.data.data);
    } catch (e) {
      setFieldValue("captcha", "");
      captchaProp.reset();
      handleApiErr(e, {
        toast,
        t: (v) => v,
        onError: (msg) => {
          if (msg === "password_mismatch") {
            setErrors({
              password: "Invalid password",
            });
            return true;
          }
          return false;
        },
      });
    }
  };

  return (
    <ModalComponent {...modalProp}>
      <ModalHeader {...modalProp}>Insert key to continue!</ModalHeader>
      <Formik initialValues={{ password: "", captcha: "" }} onSubmit={onSubmit}>
        <Form className="text-gray-800">
          <div className="bg-slate-200 mb-4 p-4 rounded">
            {message || "Enter password to continue..."}
          </div>
          <div className="mb-3">
            <FieldInput
              type="password"
              placeholder="Type password"
              name="password"
              className="!px-3 !py-2 w-full"
              required
            />
            <ErrorField name="password" />
          </div>
          <div className="flex justify-center my-6">
            <InputCaptcha action="protected_component" prop={captchaProp} />
          </div>

          <div className="flex justify-end mt-4 mb-3">
            <FormikSubmit type="submit">Continue</FormikSubmit>
          </div>
        </Form>
      </Formik>
    </ModalComponent>
  );
};

export default ProtectedModal;
